// @ts-nocheck
/* eslint-disable */
import {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import usePrices from 'hooks/equityhub/usePrices';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useOptionsCalculator, {
  OptionsCalculatorState,
} from 'hooks/optionsCalculator/useOptionsCalculator';
import {
  ProfitLossData,
  ProfitLossWithDomain,
  TableData,
} from 'types/riskEngine';
import {
  IVAdjustmentSettings,
  OC_SAVED_POSITIONS_CACHE_KEY,
  OptionType,
  Position,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import {
  buildLegExpiryCorrections,
  getAdjustedVolData,
} from 'util/optionsCalculator';
import {
  getProfitLoss,
  getSgGreeksFromDxFeedGreeks,
  getSGVolatilityData,
  getSGVolatilityDataWithOptionType,
} from 'util/riskEngine';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';
import {
  fetchExerciseStyles,
  type ExerciseStylesRecord,
} from 'util/moviefoneExerciseStyles';
import { DEFAULT_MODEL_PARAMETERS } from 'config/riskEngine';
import { OptionsCalculatorDataContext } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { OptionsCalculatorActionsContext } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { OptionsCalculatorComputedContext } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import {
  RawGreeksDataMap,
  RawGreeksObject,
  RawStatsDataMap,
} from 'types/impliedVol';
import usePositions from './usePositions';
import useEquities from 'hooks/equityhub/useEquities';
import { EquityFieldKey, LegacyEquity, SynthOIEquity } from 'types/equityhub';
import {
  dayjs,
  ET,
  getLevelsFromDataAndFields,
  isPreMarket,
  nextBusinessDay,
  prevBusinessDayOpenMarket,
} from 'util/shared';
import { FIELD_NAME_MAPPING } from 'config';
import useRiskFreeRate from 'hooks/riskFreeRate/useRiskFreeRate';
import { RISK_FREE_RATE } from 'util/BlackScholes';
import { userSubLevelState } from 'states';
import { useRecoilValue } from 'recoil';
import { SubLevel } from 'types/user';
import { IndexSymbol, IndexSymbols } from 'types/indices';
import { GreeksResult } from 'sg-risk-engine';
import { getDividends } from 'util/dividends';
import {
  buildHeatmapPrices,
  snapToListedStrikes,
  subsampleHeatmapDates,
  HeatmapMetric,
} from 'components/optionsCalculator/pnlChart/heatmapMetrics';

// Split contexts for better performance - components only re-render when their specific data changes
export interface OptionsCalculatorDataContextType {
  sym: string;
  currentPrice?: number;
  allVolDataCall: Map<string, TableData[]>;
  allVolDataPut: Map<string, TableData[]>;
  currentEquity?: LegacyEquity;
  currentKeyLevels?: { field: string; value: number; name: string }[];
  legs: PositionLeg[];
  shares: PositionShares[];
  ivSettings: IVAdjustmentSettings[];
  baseDate: number;
  allPrices?: Map<string, number>;
  allGreeksData?: Map<string, RawGreeksDataMap>;
  allStatsData?: Map<string, RawStatsDataMap>;
  allEquities?: Map<string, LegacyEquity | SynthOIEquity>;
  isLoading: boolean;
  error: Error | null;
  enabledHeatmapMetrics: HeatmapMetric[];
}

export interface OptionsCalculatorActionsContextType {
  handleSymChange: (newSym: string) => void;
  getRiskFreeRate: (tte: number) => number;
  addLeg: (leg: PositionLeg) => void;
  removeLeg: (legId: number) => void;
  updateLeg: (legId: number, updatedFields: Partial<PositionLeg>) => void;
  updateLegsBulk: (
    legIds: number[],
    updatedFields: Partial<PositionLeg>,
  ) => void;
  clearLegs: () => void;
  removeLegsBulk: (legIds: number[]) => void;
  updateShares: (shares: PositionShares[]) => void;
  updateIVSettings: (ivSettings: Partial<IVAdjustmentSettings>) => void;
  updateBaseDate: (newBaseDate: number) => void;
  getSavedPositions: () => Promise<Position[]>;
  savePosition: (
    position: Position,
    sym: string,
    name: string,
  ) => Promise<Position>;
  editSavedPosition: (
    position: Partial<Position>,
    sym: string,
  ) => Promise<Position>;
  deleteSavedPosition: (positionId: number) => Promise<void>;
  setActivePosition: (position: Position) => void;
  setEnabledHeatmapMetrics: (metrics: HeatmapMetric[]) => void;
}

export interface PnlHeatmapCell {
  // Position P/L in dollars.
  pnl: number;
  // Quantity-weighted position greeks as returned by the risk engine
  // (per-share delta/gamma; vega per vol point and theta per day). The heatmap
  // component converts these into dollar-notional terms for display.
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
}

export interface PnlHeatmapData {
  // Date column timestamps (ms), from now through the earliest expiry. A
  // shares-only position has one Now column because shares do not expire.
  dates: number[];
  // Underlying price for each row, ordered high → low (top row = highest price).
  prices: number[];
  // cells[row][col] = position metrics at that price/date, or null.
  cells: (PnlHeatmapCell | null)[][];
}

export interface OptionsCalculatorComputedContextType {
  pnlChartRef: React.RefObject<HTMLDivElement>;
  profitLoss: ProfitLossWithDomain | null;
  profitLossAtExpiration: ProfitLossWithDomain | null;
  isCalculating: boolean;
  calculationError: string | null;
  hasValidInputs: boolean;
  unadjustedIvPnl: ProfitLossWithDomain | null;
  isCalculatingUnadjustedIv: boolean;
  calculationErrorUnadjustedIv: string | null;
  // Computes a price × date grid of position P/L for the heatmap view.
  // Evaluated lazily by the heatmap component so the graph view pays nothing.
  getPnlHeatmap: (
    numDateCols?: number,
    numPriceRows?: number,
    rangeFactor?: number,
  ) => PnlHeatmapData | null;
}

interface OptionsCalculatorProviderProps {
  children: ReactNode;
}

const PRE_MARKET_SYMS_RE = /^(?:SPX|NDX|RUT|VIX|\/ES[HMUZ]\d+:XCME)$/i;

/**
 * Standalone-page entry point: backs the calculator with the URL-synced
 * state hook (a single global instance per page). Canvas instead uses
 * `CanvasOptionsCalculatorProvider`, which feeds `OptionsCalculatorProviderBase`
 * with per-instance state stored on the component's own config.
 */
export const OptionsCalculatorProvider: FC<OptionsCalculatorProviderProps> = ({
  children,
}) => {
  const state = useOptionsCalculator();
  return (
    <OptionsCalculatorProviderBase state={state}>
      {children}
    </OptionsCalculatorProviderBase>
  );
};

interface OptionsCalculatorProviderBaseProps {
  children: ReactNode;
  state: OptionsCalculatorState;
}

/**
 * Holds all the data fetching (prices, Greeks, equities, dividends) and PnL
 * computation shared by every options calculator instance, regardless of
 * where its position state (`state`) comes from.
 */
export const OptionsCalculatorProviderBase: FC<
  OptionsCalculatorProviderBaseProps
> = ({ children, state }) => {
  const { getCurrentGreeksData, getDailyGreeksData, getStatisticsData } =
    useImpliedVolatility({
      bypassAccessCheck: true,
    });
  const { getPrices } = usePrices({ bypassAccessCheck: true });
  const { fetchEquities } = useEquities();
  const userSubLevel = useRecoilValue(userSubLevelState);
  const pnlChartRef = useRef<HTMLDivElement | null>(null);

  const [getRiskFreeRate, setGetRiskFreeRate] = useState(
    () => (_: number) => RISK_FREE_RATE,
  );

  const {
    sym,
    legs,
    shares,
    ivSettings,
    baseDate,
    handleSymChange,
    addLeg,
    removeLeg,
    updateLeg,
    updateLegsBulk,
    updateLegsIndividually,
    clearLegs,
    removeLegsBulk,
    updateShares,
    updateIVSettings,
    updateBaseDate,
    setActivePosition,
    enabledHeatmapMetrics,
    setEnabledHeatmapMetrics,
  } = state;

  const {
    getSavedPositions,
    savePosition,
    editSavedPosition,
    deleteSavedPosition,
  } = usePositions();

  // Create stable references for expensive operations with proper deep comparison
  const stableLegs = useMemo(() => {
    return legs;
  }, [legs]);

  const stableShares = useMemo(() => {
    return shares;
  }, [shares]);

  const stableIVSettings = useMemo(() => {
    return ivSettings;
  }, [ivSettings]);

  // Query for saved positions
  const { data: savedPositions = [] } = useQuery<Position[]>({
    queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
    queryFn: getSavedPositions,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Fetch prices - only when sym is valid
  const { data: allPrices, isLoading: isPricesLoading } = useQuery({
    queryKey: ['stock-prices', sym],
    queryFn: () => getPrices([]),
    enabled: !!sym && sym.length > 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  const {
    data: allEquities,
    isLoading: isEquitiesLoading,
    error: equitiesError,
  } = useQuery({
    queryKey: ['oc_equities'],
    queryFn: () => fetchEquities(false, undefined, true),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  const currentEquity = useMemo(() => {
    return allEquities ? (allEquities.get(sym) as LegacyEquity) : undefined;
  }, [allEquities, sym]);

  const currentPrice = useMemo(() => {
    return sym && allPrices ? allPrices.get(sym) : undefined;
  }, [allPrices, sym]);

  const currentKeyLevels = useMemo(() => {
    if (!currentEquity) {
      return undefined;
    }
    const result = getLevelsFromDataAndFields<LegacyEquity>(
      FIELD_NAME_MAPPING,
      currentEquity,
    );
    // for standard users, only return key levels for indices
    if (userSubLevel < SubLevel.PRO) {
      return IndexSymbols.has(currentEquity[EquityFieldKey.sym] as IndexSymbol)
        ? result
        : undefined; // return undefined for non-indices
    }
    return result; // return all key levels for PRO and above
  }, [currentEquity, userSubLevel]);

  // Fetch Greeks data for all symbols (active position + saved positions)
  const allSymbols = useMemo(() => {
    const symbols = new Set<string>();

    // Only add sym if it's valid
    if (sym && sym.length > 0) {
      symbols.add(sym);
    }

    // Add symbols from saved positions
    savedPositions.forEach((position: Position) => {
      if (position.sym && position.sym.length > 0) {
        symbols.add(position.sym);
      }
    });

    return Array.from(symbols).sort(); // Sort for consistent query key
  }, [savedPositions, sym]);

  // Fetch Greeks data for all symbols with continuous polling every 30 seconds
  const {
    data: allGreeksData,
    isLoading: isGreeksLoading,
    error: greeksError,
  } = useQuery<Map<string, RawGreeksDataMap>>({
    queryKey: ['greeks', allSymbols],
    queryFn: async () => {
      if (allSymbols.length === 0) return new Map();

      const greeksPromises = allSymbols.map(async (symbol) => {
        try {
          const greeksData =
            isPreMarket() && !PRE_MARKET_SYMS_RE.test(symbol)
              ? await getDailyGreeksData(
                  prevBusinessDayOpenMarket().format('YYYY-MM-DD'),
                  symbol,
                )
              : await getCurrentGreeksData(symbol);

          return [symbol, greeksData] as [string, RawGreeksDataMap];
        } catch (error) {
          console.warn(`Failed to fetch Greeks for ${symbol}:`, error);
          return [symbol, null] as [string, RawGreeksDataMap | null];
        }
      });

      const results = await Promise.all(greeksPromises);
      const greeksMap = new Map<string, RawGreeksDataMap>();

      results.forEach(([symbol, greeksData]) => {
        if (greeksData) {
          greeksMap.set(symbol, greeksData);
        }
      });

      return greeksMap;
    },
    enabled: allSymbols.length > 0,
    staleTime: 0, // Always consider data stale to enable refetching
    gcTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: false,
    refetchInterval: 30_000, // Poll every 30 seconds
    retry: 1,
    retryDelay: 1_000,
  });

  // Fetch stats data for all symbols
  const {
    data: allStatsData,
    isLoading: isStatsLoading,
    error: statsError,
  } = useQuery<Map<string, RawStatsDataMap>>({
    queryKey: ['stats', allSymbols],
    queryFn: async () => {
      if (allSymbols.length === 0) return new Map();

      const statsPromises = allSymbols.map(async (symbol) => {
        try {
          const statsData = await getStatisticsData(symbol);
          return [symbol, statsData] as [string, RawStatsDataMap];
        } catch (error) {
          console.warn(`Failed to fetch stats for ${symbol}:`, error);
          return [symbol, null] as [string, RawStatsDataMap | null];
        }
      });

      const results = await Promise.all(statsPromises);
      const statsMap = new Map<string, RawStatsDataMap>();

      results.forEach(([symbol, statsData]) => {
        if (statsData) {
          statsMap.set(symbol, statsData);
        }
      });

      return statsMap;
    },
    enabled: allSymbols.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1_000,
  });

  const { data: allExerciseStyles } = useQuery<
    Map<string, ExerciseStylesRecord | null>
  >({
    queryKey: ['exercise-styles', allSymbols],
    queryFn: async () => {
      if (allSymbols.length === 0) return new Map();

      const stylesPromises = allSymbols.map(async (symbol) => {
        const styles = await fetchExerciseStyles(symbol);
        return [symbol, styles] as const;
      });

      return new Map(await Promise.all(stylesPromises));
    },
    enabled: allSymbols.length > 0,
    staleTime: 5 * 60 * 1_000,
    gcTime: 30 * 60 * 1_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const { data: symbolDividends = [], isLoading: isDividendsLoading } =
    useQuery({
      queryKey: ['dividends', sym],
      queryFn: async () => {
        if (!sym || sym.length === 0) return [];
        try {
          return await getDividends(sym);
        } catch (error) {
          console.warn(`Failed to fetch dividends for ${sym}:`, error);
          return [];
        }
      },
      enabled: !!sym && sym.length > 0,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 1000,
    });

  const dividendData = useMemo(() => {
    return transformDividendsToRiskEngineFormat(symbolDividends);
  }, [symbolDividends]);

  // Calculate vol data for all symbols - memoized expensive operation
  const { allVolDataCall, allVolDataPut } = useMemo(() => {
    const volDataCallMap = new Map<string, TableData[]>();
    const volDataPutMap = new Map<string, TableData[]>();

    if (!allGreeksData || !allPrices) {
      return { allVolDataCall: volDataCallMap, allVolDataPut: volDataPutMap };
    }

    allSymbols.forEach((symbol) => {
      const greeksData = allGreeksData.get(symbol);
      const symbolPrice = allPrices.get(symbol);

      if (greeksData && symbolPrice) {
        const exerciseStyles = allExerciseStyles?.get(symbol) ?? null;
        const volDataCall = getSGVolatilityDataWithOptionType(
          greeksData,
          OptionType.CALL,
          symbolPrice,
          getRiskFreeRate,
          exerciseStyles,
        );
        const volDataPut = getSGVolatilityDataWithOptionType(
          greeksData,
          OptionType.PUT,
          symbolPrice,
          getRiskFreeRate,
          exerciseStyles,
        );

        volDataCallMap.set(symbol, volDataCall);
        volDataPutMap.set(symbol, volDataPut);
      }
    });

    return { allVolDataCall: volDataCallMap, allVolDataPut: volDataPutMap };
  }, [
    allGreeksData,
    allPrices,
    allSymbols,
    getRiskFreeRate,
    dividendData,
    allExerciseStyles,
  ]);

  const allSgGreeksData = useMemo(() => {
    if (
      !allGreeksData ||
      !allVolDataCall ||
      !allVolDataPut ||
      currentPrice == null
    ) {
      return new Map<string, RawGreeksDataMap>();
    }
    const sgGreeksDataEntries = allSymbols
      .map((symbol) => {
        const greeksData = allGreeksData.get(symbol);
        const volDataCall = allVolDataCall.get(symbol);
        const volDataPut = allVolDataPut.get(symbol);
        if (greeksData && volDataCall && volDataPut) {
          return [
            symbol,
            getSgGreeksFromDxFeedGreeks(
              greeksData,
              currentPrice,
              getRiskFreeRate,
              volDataCall,
              volDataPut,
              allExerciseStyles?.get(symbol) ?? null,
            ),
          ] as const;
        }
        return null;
      })
      .filter((entry): entry is [string, RawGreeksDataMap] => entry !== null);

    return new Map(sgGreeksDataEntries);
  }, [
    allGreeksData,
    allVolDataCall,
    allVolDataPut,
    currentPrice,
    getRiskFreeRate,
    allExerciseStyles,
  ]);

  // Determine if we have valid inputs for calculations
  const hasValidCalculationInputs = useMemo(() => {
    return !!(
      sym &&
      sym.length > 0 &&
      allGreeksData &&
      allGreeksData.has(sym) &&
      currentPrice != null &&
      currentPrice > 0 &&
      (stableLegs.length > 0 || stableShares.length > 0)
    );
  }, [
    sym,
    allGreeksData,
    currentPrice,
    stableLegs.length,
    stableShares.length,
  ]);

  // Calculate earliest expiry for expiration calculation
  const earliestExpiry = useMemo(() => {
    if (stableLegs.length === 0) return baseDate;

    return stableLegs.reduce((earliest, leg) => {
      return leg.expiry < earliest ? leg.expiry : earliest;
    }, Infinity);
  }, [stableLegs, baseDate]);

  // Batched via updateLegsIndividually - looping updateLeg here would silently drop all but the last call.
  useEffect(() => {
    const symGreeks = allGreeksData?.get(sym);
    if (symGreeks == null || stableLegs.length === 0) {
      return;
    }
    const corrections = buildLegExpiryCorrections(stableLegs, symGreeks);
    if (corrections.length > 0) {
      updateLegsIndividually(
        corrections.map(({ legId, expiry }) => ({
          legId,
          fields: { expiry },
        })),
      );
    }
  }, [stableLegs, allGreeksData, sym, updateLegsIndividually]);

  const allStrikes = useMemo(() => {
    if (isGreeksLoading || greeksError || !allGreeksData?.get(sym)) {
      return [];
    }
    const greeksData: RawGreeksDataMap = allGreeksData.get(sym)!;
    return Object.values(greeksData).flatMap(
      (expirationData: RawGreeksObject) =>
        Object.keys(expirationData).map(Number),
    );
  }, [allGreeksData, sym, isGreeksLoading, greeksError]);

  // INSTANT profit loss calculation - synchronous
  const { profitLoss, calculationError } = useMemo(() => {
    if (!hasValidCalculationInputs || currentPrice == null) {
      return { profitLoss: null, calculationError: null };
    }

    const adjustedVolDataCall = getAdjustedVolData(
      allVolDataCall.get(sym) || [],
      stableIVSettings,
    );
    const adjustedVolDataPut = getAdjustedVolData(
      allVolDataPut.get(sym) || [],
      stableIVSettings,
    );

    try {
      const result = getProfitLoss(
        stableLegs,
        stableShares,
        adjustedVolDataCall,
        adjustedVolDataPut,
        allStrikes,
        {
          ...DEFAULT_MODEL_PARAMETERS,
          getRiskFreeRate,
          volatility: undefined,
          baseDate,
          currentPrice: currentPrice,
        },
      );

      return { profitLoss: result, calculationError: null };
    } catch (error) {
      return {
        profitLoss: null,
        calculationError:
          error instanceof Error ? error.message : String(error),
      };
    }
  }, [
    hasValidCalculationInputs,
    currentPrice,
    stableLegs,
    stableShares,
    allVolDataCall,
    allVolDataPut,
    allStrikes,
    sym,
    stableIVSettings,
    baseDate,
  ]);

  // Lazily computes a price × date grid of position P/L for the heatmap view.
  // Reuses the same getProfitLoss engine, evaluated once per date column.
  const getPnlHeatmap = useCallback(
    (
      numDateCols = 12,
      numPriceRows = 21,
      rangeFactor = 1,
    ): PnlHeatmapData | null => {
      if (!hasValidCalculationInputs || currentPrice == null) {
        return null;
      }

      if (numPriceRows < 2 || (stableLegs.length > 0 && numDateCols < 2)) {
        return null;
      }

      const prices = snapToListedStrikes(
        buildHeatmapPrices(
          currentPrice,
          stableLegs.map((leg) => leg.strike),
          numPriceRows,
          rangeFactor,
        ),
        allStrikes,
      );
      if (prices.length === 0) {
        return null;
      }

      const nowTs = Date.now();
      const adjustedVolDataCall = getAdjustedVolData(
        allVolDataCall.get(sym) || [],
        stableIVSettings,
      );
      const adjustedVolDataPut = getAdjustedVolData(
        allVolDataPut.get(sym) || [],
        stableIVSettings,
      );

      let dates: number[];
      if (stableLegs.length === 0) {
        // Shares have no expiration or time decay. A single Now column avoids
        // presenting an invented expiration date to the user.
        dates = [nowTs];
      } else {
        const expiry = earliestExpiry;
        if (!(expiry > nowTs)) {
          return null;
        }

        // One column per market-open day through expiry lets event days land on
        // a real column. For long-dated positions, subsample those same trading
        // timestamps instead of interpolating weekend/off-hours timestamps.
        const MAX_DAILY_COLS = 70;
        const marketCloseET = (ms: number): number =>
          dayjs(ms)
            .tz(ET)
            .hour(16)
            .minute(0)
            .second(0)
            .millisecond(0)
            .valueOf();
        const expiryDayStart = dayjs(expiry).tz(ET).startOf('day').valueOf();
        const tradingTs: number[] = [];
        let dayTs = marketCloseET(
          nextBusinessDay(dayjs(nowTs).tz(ET)).valueOf(),
        );
        let guard = 0;
        while (dayTs < expiryDayStart && guard < 10_000) {
          if (dayTs > nowTs) {
            tradingTs.push(dayTs);
          }
          dayTs = marketCloseET(nextBusinessDay(dayjs(dayTs).tz(ET)).valueOf());
          guard++;
        }

        const actualDates = [nowTs, ...tradingTs, expiry];
        dates =
          tradingTs.length <= MAX_DAILY_COLS
            ? actualDates
            : subsampleHeatmapDates(actualDates, numDateCols);
      }

      const curves = dates.map((d) => {
        try {
          return getProfitLoss(
            stableLegs,
            stableShares,
            adjustedVolDataCall,
            adjustedVolDataPut,
            allStrikes,
            {
              ...DEFAULT_MODEL_PARAMETERS,
              getRiskFreeRate,
              volatility: undefined,
              baseDate: d,
              currentPrice,
              samplePrices: prices,
            },
          );
        } catch {
          return null;
        }
      });

      type HeatmapField = 'profit' | 'delta' | 'gamma' | 'vega' | 'theta';

      const cellFromPoint = (
        point: ProfitLossData | undefined,
      ): PnlHeatmapCell | null => {
        if (point == null) {
          return null;
        }

        const fields: HeatmapField[] = [
          'profit',
          'delta',
          'gamma',
          'vega',
          'theta',
        ];
        if (
          fields.some((field) => {
            const value = point[field];
            return typeof value !== 'number' || !Number.isFinite(value);
          })
        ) {
          return null;
        }

        return {
          pnl: point.profit,
          delta: point.delta!,
          gamma: point.gamma!,
          vega: point.vega!,
          theta: point.theta!,
        };
      };

      const curvePoints = curves.map((curve) =>
        curve == null
          ? null
          : new Map(curve.profitLossData.map((point) => [point.price, point])),
      );
      const cells = prices.map((price) =>
        curvePoints.map((points): PnlHeatmapCell | null =>
          cellFromPoint(points?.get(price)),
        ),
      );

      return { dates, prices, cells };
    },
    [
      hasValidCalculationInputs,
      currentPrice,
      earliestExpiry,
      stableLegs,
      stableShares,
      allVolDataCall,
      allVolDataPut,
      allStrikes,
      sym,
      stableIVSettings,
      getRiskFreeRate,
    ],
  );

  // INSTANT profit loss at expiration calculation - synchronous
  const { profitLossAtExpiration, calculationErrorAtExpiration } =
    useMemo(() => {
      if (!hasValidCalculationInputs || currentPrice == null) {
        return {
          profitLossAtExpiration: null,
          calculationErrorAtExpiration: null,
        };
      }

      const adjustedVolDataCall = getAdjustedVolData(
        allVolDataCall.get(sym) || [],
        stableIVSettings,
      );
      const adjustedVolDataPut = getAdjustedVolData(
        allVolDataPut.get(sym) || [],
        stableIVSettings,
      );
      try {
        const result = getProfitLoss(
          stableLegs,
          stableShares,
          adjustedVolDataCall,
          adjustedVolDataPut,
          allStrikes,
          {
            ...DEFAULT_MODEL_PARAMETERS,
            volatility: undefined,
            baseDate: earliestExpiry !== Infinity ? earliestExpiry : baseDate,
            currentPrice: currentPrice,
          },
        );

        return {
          profitLossAtExpiration: result,
          calculationErrorAtExpiration: null,
        };
      } catch (error) {
        return {
          profitLossAtExpiration: null,
          calculationErrorAtExpiration:
            error instanceof Error ? error.message : String(error),
        };
      }
    }, [
      hasValidCalculationInputs,
      currentPrice,
      stableLegs,
      stableShares,
      allVolDataCall,
      allVolDataPut,
      allStrikes,
      sym,
      stableIVSettings,
      earliestExpiry,
      baseDate,
    ]);

  // INSTANT unadjusted IV profit loss calculation - synchronous
  const { unadjustedIvPnl, calculationErrorUnadjustedIv } = useMemo(() => {
    if (!hasValidCalculationInputs || currentPrice == null) {
      return { unadjustedIvPnl: null, calculationErrorUnadjustedIv: null };
    }

    const volDataCall = allVolDataCall.get(sym) || [];
    const volDataPut = allVolDataPut.get(sym) || [];
    try {
      const result = getProfitLoss(
        stableLegs,
        stableShares,
        volDataCall, // Unadjusted IV
        volDataPut, // Unadjusted IV
        allStrikes,
        {
          ...DEFAULT_MODEL_PARAMETERS,
          volatility: undefined,
          baseDate,
          currentPrice: currentPrice,
        },
      );

      return { unadjustedIvPnl: result, calculationErrorUnadjustedIv: null };
    } catch (error) {
      return {
        unadjustedIvPnl: null,
        calculationErrorUnadjustedIv:
          error instanceof Error ? error.message : String(error),
      };
    }
  }, [
    hasValidCalculationInputs,
    currentPrice,
    stableLegs,
    stableShares,
    allVolDataCall,
    allVolDataPut,
    allStrikes,
    sym,
    earliestExpiry,
    baseDate,
  ]);

  // Memoize context values to prevent unnecessary re-renders
  const dataContextValue = useMemo(
    () => ({
      sym,
      allEquities,
      allVolDataCall,
      allVolDataPut,
      currentPrice,
      currentEquity,
      currentKeyLevels,
      legs: stableLegs,
      shares: stableShares,
      ivSettings: stableIVSettings,
      baseDate,
      allPrices,
      allGreeksData: allSgGreeksData,
      allStatsData,
      isLoading:
        isGreeksLoading ||
        isStatsLoading ||
        isPricesLoading ||
        isEquitiesLoading ||
        isDividendsLoading,
      error: greeksError || statsError || equitiesError,
      enabledHeatmapMetrics,
    }),
    [
      sym,
      currentPrice,
      allVolDataCall,
      allVolDataPut,
      currentEquity,
      currentKeyLevels,
      stableLegs,
      stableShares,
      stableIVSettings,
      baseDate,
      allPrices,
      allSgGreeksData,
      allStatsData,
      isGreeksLoading,
      isStatsLoading,
      isPricesLoading,
      allEquities,
      greeksError,
      statsError,
      isEquitiesLoading,
      equitiesError,
      isDividendsLoading,
      enabledHeatmapMetrics,
    ],
  );

  const actionsContextValue = useMemo(
    () => ({
      handleSymChange,
      getRiskFreeRate,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateIVSettings,
      updateBaseDate,
      getSavedPositions,
      savePosition,
      editSavedPosition,
      deleteSavedPosition,
      setActivePosition,
      setEnabledHeatmapMetrics,
    }),
    [
      getRiskFreeRate,
      handleSymChange,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateIVSettings,
      updateBaseDate,
      setActivePosition,
      setEnabledHeatmapMetrics,
    ],
  );

  const computedContextValue = useMemo(
    () => ({
      pnlChartRef,
      profitLoss: hasValidCalculationInputs ? profitLoss : null,
      profitLossAtExpiration: hasValidCalculationInputs
        ? profitLossAtExpiration
        : null,
      isCalculating: false, // No longer async, so never calculating
      calculationError:
        calculationError || calculationErrorAtExpiration || null,
      hasValidInputs: hasValidCalculationInputs,
      unadjustedIvPnl: hasValidCalculationInputs ? unadjustedIvPnl : null,
      isCalculatingUnadjustedIv: false, // No longer async, so never calculating
      calculationErrorUnadjustedIv: hasValidCalculationInputs
        ? calculationErrorUnadjustedIv
        : null,
      getPnlHeatmap,
    }),
    [
      pnlChartRef,
      hasValidCalculationInputs,
      profitLoss,
      profitLossAtExpiration,
      unadjustedIvPnl,
      calculationError,
      calculationErrorAtExpiration,
      calculationErrorUnadjustedIv,
      getPnlHeatmap,
    ],
  );

  return (
    <OptionsCalculatorDataContext.Provider value={dataContextValue}>
      <OptionsCalculatorActionsContext.Provider value={actionsContextValue}>
        <OptionsCalculatorComputedContext.Provider value={computedContextValue}>
          {children}
        </OptionsCalculatorComputedContext.Provider>
      </OptionsCalculatorActionsContext.Provider>
    </OptionsCalculatorDataContext.Provider>
  );
};
